import { AppState, Activity, ActivityComment } from '@/types';
import { AppAction } from '../types';
import { saveActivityToSupabase, updateActivityLikesInSupabase, saveActivityCommentToSupabase, deleteActivityFromSupabase } from '@/utils/supabaseDataSaver';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentTier } from '@/lib/tierSystem';
import { toast } from 'sonner';
import { QueryClient } from '@tanstack/react-query';

// Shared query client reference for cache invalidation
let queryClientRef: QueryClient | null = null;
export function setQueryClientRef(qc: QueryClient) {
  queryClientRef = qc;
}

// Deduplicate W$ conversion calls (React StrictMode runs reducers twice)
let _lastConversionKey = '';

// Trigger live W$ conversion after activity is saved
function triggerWDollarConversion(userId: string) {
  const key = `${userId}-${Math.floor(Date.now() / 2000)}`; // 2s dedup window
  if (key === _lastConversionKey) return;
  _lastConversionKey = key;

  supabase.rpc('convert_activity_to_w_dollar', { p_user_id: userId })
    .then(({ data, error }) => {
      if (error) {
        console.error('❌ W$ conversion RPC error:', error);
        return;
      }
      const result = data as Record<string, unknown> | null;
      if (result?.converted) {
        queryClientRef?.invalidateQueries({ queryKey: ['win-credits'] });
        queryClientRef?.invalidateQueries({ queryKey: ['win-credits-transactions'] });
      }
    });
}

// Reverse W$ conversion after activity undo
function triggerWDollarReversal(userId: string, pointsRemoved: number) {
  supabase.rpc('reverse_w_dollar_on_undo', { p_user_id: userId, p_points_removed: pointsRemoved })
    .then(({ data, error }) => {
      if (error) {
        console.error('❌ W$ reversal RPC error:', error);
        return;
      }
      const result = data as Record<string, unknown> | null;
      if (result?.reversed) {
        queryClientRef?.invalidateQueries({ queryKey: ['win-credits'] });
        queryClientRef?.invalidateQueries({ queryKey: ['win-credits-transactions'] });
      }
    });
}

export function handleUndoLastActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'UNDO_LAST_ACTIVITY' }>
): AppState {
  const { activityId, points, activityCredits } = action.payload;

  // Remove the activity from the activities array
  const updatedActivities = state.activities.filter(a => a.id !== activityId);

  // Find the deleted activity to get its name for updating counts
  const deletedActivity = state.activities.find(a => a.id === activityId);
  
  // Update activity counts
  const updatedActivityCounts = { ...state.activityCounts };
  if (deletedActivity && updatedActivityCounts[deletedActivity.name]) {
    updatedActivityCounts[deletedActivity.name] = Math.max(0, updatedActivityCounts[deletedActivity.name] - 1);
  }

  // Delete activity from database
  deleteActivityFromSupabase(activityId).then(() => {
    // Reverse W$ conversion after activity is removed
    if (state.user?.id) {
      triggerWDollarReversal(state.user.id, points);
    }
  }).catch(error => {
    console.error('❌ Failed to delete activity from database:', error);
  });

  const newTotalPoints = Math.max(0, state.totalPoints - points);
  const newState = {
    ...state,
    activities: updatedActivities,
    activityCounts: updatedActivityCounts,
    totalPoints: newTotalPoints,
    currentTier: getCurrentTier(newTotalPoints),
    currentWeekPoints: Math.max(0, state.currentWeekPoints - points),
    experience: Math.max(0, state.experience - points), // Deduct experience (XP = points)
    activityCredits: Math.max(0, (state.activityCredits || 0) - activityCredits),
  };

// Save updated state to database (async)
  if (state.user?.id) {
    import('@/utils/supabaseDataSaver').then(({ saveUserDataToSupabase }) => {
      saveUserDataToSupabase(state.user!.id, newState, { strategy: 'prefer-app' }).catch(error => {
        console.error('❌ Failed to save updated user stats after undo:', error);
      });
    });
  }

  return newState;
}

export function handleUndoBulkActivities(
  state: AppState,
  action: Extract<AppAction, { type: 'UNDO_BULK_ACTIVITIES' }>
): AppState {
  const { activityIds } = action.payload;
  const idSet = new Set(activityIds);

  const deletedActivities = state.activities.filter(a => idSet.has(a.id));
  const updatedActivities = state.activities.filter(a => !idSet.has(a.id));

  const updatedActivityCounts = { ...state.activityCounts };
  let totalPointsRemoved = 0;
  for (const act of deletedActivities) {
    totalPointsRemoved += act.points;
    if (updatedActivityCounts[act.name]) {
      updatedActivityCounts[act.name] = Math.max(0, updatedActivityCounts[act.name] - 1);
    }
  }

  const creditsToDeduct = totalPointsRemoved * 0.1;
  const newTotalPoints = Math.max(0, state.totalPoints - totalPointsRemoved);

  if (state.user?.id) {
    triggerWDollarReversal(state.user.id, totalPointsRemoved);
  }

  const newState = {
    ...state,
    activities: updatedActivities,
    activityCounts: updatedActivityCounts,
    totalPoints: newTotalPoints,
    currentTier: getCurrentTier(newTotalPoints),
    currentWeekPoints: Math.max(0, state.currentWeekPoints - totalPointsRemoved),
    experience: Math.max(0, state.experience - totalPointsRemoved),
    activityCredits: Math.max(0, (state.activityCredits || 0) - creditsToDeduct),
  };

  if (state.user?.id) {
    import('@/utils/supabaseDataSaver').then(({ saveUserDataToSupabase }) => {
      saveUserDataToSupabase(state.user!.id, newState, { strategy: 'prefer-app' }).catch(error => {
        console.error('❌ Failed to save updated user stats after bulk undo:', error);
      });
    });
  }

  return newState;
}

export function handleLogBulkActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'LOG_BULK_ACTIVITY' }>
): AppState {
  if (!state.user || !state.isAuthenticated) return state;

  const { activityType, quantity, activityId } = action.payload;
  const totalPoints = activityType.points * quantity;
  const now = new Date();

  const newActivity: Activity = {
    id: activityId,
    name: activityType.name,
    activityType: activityType,
    points: totalPoints,
    timestamp: now,
    description: activityType.description,
    userId: state.user.id,
    userName: state.user.name,
    likes: [],
    likedBy: [],
    comments: [],
    duration: null,
    notes: null,
    photoUrl: null,
    isOwnActivity: true,
  };

  // Save to DB
  saveActivityToSupabase({ ...newActivity } as any).catch(error => {
    console.error('❌ Failed to save bulk activity to database:', error);
  }).then(() => {
    if (state.user?.id) triggerWDollarConversion(state.user.id);
  });

  // Credits
  const creditsEarned = totalPoints * 0.1;
  const newBalance = state.activityCredits + creditsEarned;
  if (state.user?.id && creditsEarned > 0) {
    supabase.from('credit_transactions').insert({
      user_id: state.user.id,
      amount: creditsEarned,
      balance_after: newBalance,
      transaction_type: 'activity_earned',
      description: `${quantity}× ${activityType.name} (+${totalPoints} pts)`,
      performed_by: state.user.id,
    }).then(({ error }) => {
      if (error) console.error('Failed to log credit transaction:', error);
    });
  }

  const updatedActivityCounts = {
    ...state.activityCounts,
    [activityType.name]: (state.activityCounts[activityType.name] || 0) + quantity,
  };

  const newWeekPoints = state.currentWeekPoints + totalPoints;
  const justReachedGoal = newWeekPoints >= state.weeklyGoal &&
    state.currentWeekPoints < state.weeklyGoal &&
    !state.weeklyBonusAwarded;
  const weeklyBonus = justReachedGoal ? Math.floor(state.weeklyGoal * 0.1) : 0;

  const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
  const toSGTDateStr = (d: Date) => new Date(d.getTime() + SGT_OFFSET_MS).toISOString().slice(0, 10);
  const todaySGT = toSGTDateStr(now);
  const isNewStreakDay = !state.lastStreakDate || state.lastStreakDate !== todaySGT;
  let newStreak = state.streak;
  if (isNewStreakDay) {
    if (!state.lastStreakDate) {
      newStreak = 1;
    } else {
      const yesterdaySGT = toSGTDateStr(new Date(now.getTime() - 24 * 60 * 60 * 1000));
      newStreak = state.lastStreakDate === yesterdaySGT ? state.streak + 1 : 1;
    }
  }

  const newTotalPoints = state.totalPoints + totalPoints;
  return {
    ...state,
    activities: [newActivity, ...state.activities],
    totalPoints: newTotalPoints,
    currentTier: getCurrentTier(newTotalPoints),
    currentWeekPoints: newWeekPoints,
    experience: state.experience + totalPoints,
    activityCredits: newBalance,
    streak: newStreak,
    bestStreak: Math.max(state.bestStreak || 0, newStreak),
    lastStreakDate: isNewStreakDay ? todaySGT : state.lastStreakDate,
    activitiesLoggedToday: state.activitiesLoggedToday + 1,
    activityCounts: updatedActivityCounts,
    weeklyBonusAwarded: justReachedGoal || state.weeklyBonusAwarded,
    weeklyBonusEarned: state.weeklyBonusEarned + weeklyBonus,
  };
}

export function handleLogActivity(
  state: AppState, 
  action: Extract<AppAction, { type: 'LOG_ACTIVITY' }>
): AppState {
  if (!state.user || !state.isAuthenticated) {
    console.log('❌ User not authenticated in handleLogActivity');
    return state;
  }

  const { activityType } = action.payload;
  const now = new Date();
  
  // Create new activity
  const newActivity: Activity = {
    id: action.payload.activityId || crypto.randomUUID(),
    name: activityType.name,
    activityType: activityType,
    points: activityType.points,
    timestamp: now,
    description: activityType.description,
    userId: state.user.id,
    userName: state.user.name,
    likes: [],
    likedBy: [],
    comments: [],
    duration: null,
    notes: null,
    photoUrl: null,
    isOwnActivity: true
  };

  // Update activity counts
  const updatedActivityCounts = {
    ...state.activityCounts,
    [activityType.name]: (state.activityCounts[activityType.name] || 0) + 1
  };

  // Calculate streak using Singapore Time (SGT = UTC+8) for consistency
  const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
  const toSGTDateStr = (d: Date) => new Date(d.getTime() + SGT_OFFSET_MS).toISOString().slice(0, 10);
  const todaySGT = toSGTDateStr(now);
  const lastStreakDate = state.lastStreakDate;
  const isNewStreakDay = !lastStreakDate || lastStreakDate !== todaySGT;

  // Check if streak should continue or reset
  let newStreak = state.streak;
  if (isNewStreakDay) {
    if (!lastStreakDate) {
      newStreak = 1;
    } else {
      const yesterdaySGT = toSGTDateStr(new Date(now.getTime() - 24 * 60 * 60 * 1000));
      newStreak = lastStreakDate === yesterdaySGT ? state.streak + 1 : 1;
    }
  }
  const newLastStreakDate = isNewStreakDay ? todaySGT : lastStreakDate;

  console.log('🎯 Direct activity logged (POINTS + XP):', {
    activity: activityType.name,
    pointsGained: activityType.points,
    xpGained: activityType.points, // XP = Points (1:1 ratio)
    goldGained: 0
  });

  // Save activity to database
  saveActivityToSupabase(newActivity).catch(error => {
    console.error('❌ Failed to save activity to database:', error);
  }).then(() => {
    // Trigger live W$ conversion after activity is saved
    if (state.user?.id) {
      triggerWDollarConversion(state.user.id);
    }
  });

  // Calculate new week points
  const newWeekPoints = state.currentWeekPoints + activityType.points;
  
  // Check if we just reached the weekly goal and haven't awarded bonus yet
  const justReachedGoal = newWeekPoints >= state.weeklyGoal && 
                          state.currentWeekPoints < state.weeklyGoal &&
                          !state.weeklyBonusAwarded;
  
  // Calculate weekly bonus if goal just reached
  const weeklyBonus = justReachedGoal ? Math.floor(state.weeklyGoal * 0.1) : 0;

  // Log credit transaction for activity earned
  const creditsEarned = activityType.points * 0.1 + weeklyBonus;
  const newBalance = state.activityCredits + creditsEarned;
  if (state.user?.id && creditsEarned > 0) {
    supabase.from('credit_transactions').insert({
      user_id: state.user.id,
      amount: creditsEarned,
      balance_after: newBalance,
      transaction_type: 'activity_earned',
      description: `${activityType.name} (+${activityType.points} pts)${weeklyBonus > 0 ? ` + Weekly Goal Bonus` : ''}`,
      performed_by: state.user.id,
    }).then(({ error }) => {
      if (error) console.error('Failed to log credit transaction:', error);
    });
  }

  if (justReachedGoal) {
    console.log('🎉 Weekly goal reached! Awarding bonus:', weeklyBonus, 'credits');
    // Import and use toast
    import('@/hooks/use-toast').then(({ toast }) => {
      toast({
        title: "🎉 Weekly Goal Achieved!",
        description: `You earned ${weeklyBonus} bonus activity credits!`,
        duration: 5000,
      });
    });
  }

  const newTotalPoints = state.totalPoints + activityType.points;
  return {
    ...state,
    activities: [newActivity, ...state.activities],
    totalPoints: newTotalPoints,
    currentTier: getCurrentTier(newTotalPoints),
    currentWeekPoints: newWeekPoints,
    experience: state.experience + activityType.points,
    activityCredits: newBalance,
    streak: newStreak,
    bestStreak: Math.max(state.bestStreak || 0, newStreak),
    lastStreakDate: newLastStreakDate,
    activitiesLoggedToday: state.activitiesLoggedToday + 1,
    activityCounts: updatedActivityCounts,
    weeklyBonusAwarded: justReachedGoal || state.weeklyBonusAwarded,
    weeklyBonusEarned: state.weeklyBonusEarned + weeklyBonus,
  };
}

export function handleLogEnhancedActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'LOG_ENHANCED_ACTIVITY' }>
): AppState {
  if (!state.user || !state.isAuthenticated) {
    return state;
  }

  const { activityType, duration, notes, photoUrl, locationData, policyName, annualizedPremium } = action.payload;
  const now = new Date();
  
  // Create enhanced activity
  const newActivity: Activity = {
    id: action.payload.activityId || crypto.randomUUID(),
    name: activityType.name,
    activityType: activityType,
    points: activityType.points,
    timestamp: now,
    description: activityType.description,
    userId: state.user.id,
    userName: state.user.name,
    likes: [],
    likedBy: [],
    comments: [],
    duration,
    notes,
    photoUrl,
    latitude: locationData?.latitude,
    longitude: locationData?.longitude,
    locationAccuracy: locationData?.accuracy,
    isOwnActivity: true,
    // Closed deal fields
    policyName,
    annualizedPremium
  };

  // Enhanced activities get slightly better reward tokens but still no XP/Gold
  const bonusMultiplier = 1.2; // Reduced bonus since no XP/Gold

  // Calculate streak using Singapore Time (SGT = UTC+8) for consistency
  const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
  const toSGTDateStr = (d: Date) => new Date(d.getTime() + SGT_OFFSET_MS).toISOString().slice(0, 10);
  const todaySGT = toSGTDateStr(now);
  const lastStreakDate = state.lastStreakDate;
  const isNewStreakDay = !lastStreakDate || lastStreakDate !== todaySGT;

  let newStreak = state.streak;
  if (isNewStreakDay) {
    if (!lastStreakDate) {
      newStreak = 1;
    } else {
      const yesterdaySGT = toSGTDateStr(new Date(now.getTime() - 24 * 60 * 60 * 1000));
      newStreak = lastStreakDate === yesterdaySGT ? state.streak + 1 : 1;
    }
  }
  const newLastStreakDate = isNewStreakDay ? todaySGT : lastStreakDate;

  // Update activity counts
  const updatedActivityCounts = {
    ...state.activityCounts,
    [activityType.name]: (state.activityCounts[activityType.name] || 0) + 1
  };

  console.log('📝 Enhanced activity logged (POINTS + XP):', {
    activity: activityType.name,
    pointsGained: activityType.points,
    xpGained: activityType.points, // XP = Points (1:1 ratio)
    goldGained: 0,
    policyName,
    annualizedPremium
  });

  // Save activity to database
  saveActivityToSupabase(newActivity).then(() => {
    // Trigger live W$ conversion after activity is saved
    if (state.user?.id) {
      triggerWDollarConversion(state.user.id);
    }
    // After saving, send case-closed notification if this is a "Closed" activity
    if (activityType.name === 'Closed' && state.user?.id) {
      supabase.functions.invoke('send-case-closed-notification', {
        body: {
          userId: state.user.id,
          policyName,
          annualizedPremium,
          activityId: newActivity.id,
        },
      }).then(res => {
        console.log('📧 Case-closed notification result:', res.data);
      }).catch(err => {
        console.error('❌ Failed to send case-closed notifications:', err);
      });
    }
  }).catch(error => {
    console.error('❌ Failed to save activity to database:', error);
  });

  // Calculate new week points
  const newWeekPoints = state.currentWeekPoints + activityType.points;
  
  // Check if we just reached the weekly goal and haven't awarded bonus yet
  const justReachedGoal = newWeekPoints >= state.weeklyGoal && 
                          state.currentWeekPoints < state.weeklyGoal &&
                          !state.weeklyBonusAwarded;
  
  // Calculate weekly bonus if goal just reached
  const weeklyBonus = justReachedGoal ? Math.floor(state.weeklyGoal * 0.1) : 0;

  // Log credit transaction for enhanced activity earned
  const creditsEarned = activityType.points * bonusMultiplier * 0.1 + weeklyBonus;
  const newBalance = state.activityCredits + creditsEarned;
  if (state.user?.id && creditsEarned > 0) {
    supabase.from('credit_transactions').insert({
      user_id: state.user.id,
      amount: creditsEarned,
      balance_after: newBalance,
      transaction_type: 'activity_earned',
      description: `${activityType.name} (+${activityType.points} pts)${weeklyBonus > 0 ? ` + Weekly Goal Bonus` : ''}`,
      performed_by: state.user.id,
    }).then(({ error }) => {
      if (error) console.error('Failed to log credit transaction:', error);
    });
  }
  
  if (justReachedGoal) {
    console.log('🎉 Weekly goal reached! Awarding bonus:', weeklyBonus, 'credits');
    import('@/hooks/use-toast').then(({ toast }) => {
      toast({
        title: "🎉 Weekly Goal Achieved!",
        description: `You earned ${weeklyBonus} bonus activity credits!`,
        duration: 5000,
      });
    });
  }

  const newTotalPoints = state.totalPoints + activityType.points;
  return {
    ...state,
    activities: [newActivity, ...state.activities],
    totalPoints: newTotalPoints,
    currentTier: getCurrentTier(newTotalPoints),
    currentWeekPoints: newWeekPoints,
    experience: state.experience + activityType.points,
    activityCredits: newBalance,
    streak: newStreak,
    bestStreak: Math.max(state.bestStreak || 0, newStreak),
    lastStreakDate: newLastStreakDate,
    activitiesLoggedToday: state.activitiesLoggedToday + 1,
    activityCounts: updatedActivityCounts,
    weeklyBonusAwarded: justReachedGoal || state.weeklyBonusAwarded,
    weeklyBonusEarned: state.weeklyBonusEarned + weeklyBonus,
  };
}

export function handleLikeActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'LIKE_ACTIVITY' }>
): AppState {
  const { activityId, userId } = action.payload;
  
  const newState = {
    ...state,
    activities: state.activities.map(activity => {
      if (activity.id === activityId) {
        const currentLikes = activity.likes || [];
        const isLiked = currentLikes.includes(userId);
        const newLikes = isLiked 
          ? currentLikes.filter(id => id !== userId)
          : [...currentLikes, userId];
        
        // Save likes to database
        updateActivityLikesInSupabase(activityId, newLikes).catch(error => {
          console.error('❌ Failed to update likes in database:', error);
        });
        
        return {
          ...activity,
          likes: newLikes
        };
      }
      return activity;
    })
  };
  
  return newState;
}

export function handleCommentActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'COMMENT_ACTIVITY' }>
): AppState {
  const { activityId, comment } = action.payload;
  
  // Save comment to database
  saveActivityCommentToSupabase(activityId, comment).catch(error => {
    console.error('❌ Failed to save comment to database:', error);
  });
  
  return {
    ...state,
    activities: state.activities.map(activity => {
      if (activity.id === activityId) {
        return {
          ...activity,
          comments: [...(activity.comments || []), comment]
        };
      }
      return activity;
    })
  };
}
